import moment from "moment";
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import Swal from "sweetalert2";
import SuccessSRC from "../../../assets/jsons/Success.json";
import { Button } from "../../../components/Button";
import { FooterPaginas } from "../../../components/FooterPaginas/FooterPaginas";
import Form from "../../../components/Form";
import { Input } from "../../../components/Input";
import ModalLayoutImpressao from "../../../components/ModalLayoutImpressao/ModalLayoutImpressao";
import PopoverInfo from "../../../components/PopoverInfo/PopoverInfo";
import PrintableSacola from "../../../components/PrintableSacola/PrintableSacola";
import { SkeletonPedidoCesta } from "../../../components/Skeletons/SkeletonPedidoCesta/SkeletonPedidoCesta";
import TableProdutos from "../../../components/TableProdutos/TableProdutos";
import { carregarOrdenacao, OrdenacaoConfig } from "../../../components/TableProdutos/OrdenacaoButton/ordenacaoUtils";
import { EmpresaContext } from "../../../contexts/Empresa/EmpresaContext";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { CestasAbandonadasApi } from "../../../hooks/cestasAbandonadasApi";
import { cepMask, cnpjMask, cpfMask, phoneMask } from "../../../services/Masks";
import { CestaAbandonada } from "../../../types/CestaAbandonada";
import { LayoutImpressaoSacola, defaultLayoutSacola } from "../../../types/LayoutImpressao";
import "./styles";
import { ContainerCesta } from "./styles";
import { SelectPedidos } from "../../../components/Select/SelectPedidos/index";

interface CestaProps {}

const Cesta: React.FC<CestaProps> = ({}) => {
  const animation = SuccessSRC;

  const cestaApi = CestasAbandonadasApi();
  const {empresa, setEmpresa} = useContext(EmpresaContext);

  const { theme } = useTheme();

  const { id } = useParams<{ id: string }>();
  const [observacaoInterna, setObservacaoInterna] = useState("");
  const [cesta, setCesta] = useState<CestaAbandonada>();
  const [loading, setLoading] = useState(true);
  const [loadingPrinting, setLoadingPrinting] = useState(false);
  const [situacoes, setSituacoes] = useState<
  Array<{ value: number; name: string; quantidade?: number }>
>([]);
const [loadingSkeleton, setLoadingSkeleton] = useState(true);


  const printableRef = useRef<HTMLDivElement>(null);
  const [modalLayoutOpen, setModalLayoutOpen] = useState(false);
  const [layoutConfig, setLayoutConfig] = useState<LayoutImpressaoSacola>(() => {
    const stored = localStorage.getItem("layoutImpressaoSacolas");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return defaultLayoutSacola;
      }
    }
    return defaultLayoutSacola;
  });
  const [ordenacaoProdutos, setOrdenacaoProdutos] = useState<OrdenacaoConfig>(carregarOrdenacao);

  const [formValidObs, setFormValidObs] = useState(false);

  const cores = {
    // cores conforme o id da situação no banco
    1: "#4de889", //em aberto
    2: "#e7be00", //em contato
    3: "#ffbe9d", //nao convertido
  };

  const init = async () => {
    if (!id) return;

    const showSwalSituacao = localStorage.getItem("swal_situacao_alterada");
    if (showSwalSituacao) {
      localStorage.removeItem("swal_situacao_alterada");
      Swal.fire({
        icon: "success",
        showConfirmButton: false,
        timer: 1000,
        title: "Situação da sacola alterada com sucesso!",
      });
    }

    const [cesta, situacoes] = await Promise.all([
      cestaApi.getCestaAbandonada(id),
      cestaApi.getSituacoes()
    ]);

    setCesta(cesta);
    setObservacaoInterna(cesta.observacao);
    setLoading(false);

    setSituacoes(
      situacoes.map((situacao: any) => ({
        name: situacao.descricao,
        value: situacao.id.toString(),
      }))
    );
    setLoadingSkeleton(false);
  };

  const handleSituacaoChange = async (situacao: string) => {
    if (!id || cesta?.id_situacao?.toString() === situacao) return;
      setLoading(true);
    await cestaApi.alterarSituacao([id], Number.parseInt(situacao)).then(
      (res) => {
        Swal.fire({
          icon: "success",
          showConfirmButton: false,
          timer: 1000,
          title: "Situação da sacola alterada com sucesso!",
        });
        setCesta({ ...cesta!, id_situacao: Number.parseInt(situacao) });
        setLoading(false);
      },
      (err) => {
        Swal.fire({
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
          title: "Erro ao alterar situação da sacola!",
        });
        setLoading(false);
      }
    );
  };

  const handleOnSubmitObservacaoInterna = async (observacao: string) => {
    id &&
      cestaApi.atualizarObservacaoInternaCesta(id, observacao).then(
        (res) => {
          Swal.fire({
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
            title: "Observação interna atualizada com sucesso!",
          });
        },
        (err) => {
          Swal.fire({
            icon: "error",
            showConfirmButton: false,
            timer: 1500,
            title: "Erro ao atualizar observação interna!",
          });
        },
      );
  };

  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
    onBeforeGetContent: async () => {
      setOrdenacaoProdutos(carregarOrdenacao());
      setLoadingPrinting(true);
    },
    onAfterPrint: () => setLoadingPrinting(false),
    onPrintError: () => {
      Swal.fire({
        icon: "error",
        showConfirmButton: false,
        timer: 1500,
        title: "Erro ao imprimir sacola!",
      });
      setLoadingPrinting(false);
    },
  });

  const handleModalClose = () => {
    setModalLayoutOpen(false);
    const stored = localStorage.getItem("layoutImpressaoSacolas");
    if (stored) {
      try {
        setLayoutConfig(JSON.parse(stored));
      } catch {
        setLayoutConfig(defaultLayoutSacola);
      }
    }
  };

  const memoizedSacola = useMemo(() => cesta, [cesta]);

  useEffect(() => {
    init();
  }, []);

  return (
    <>
      {loading ? (
        <SkeletonPedidoCesta />
      ) : (
        <ContainerCesta theme={theme}>
          <div className="flex-container">
            <div className="subtitulo-cesta fonteTitulo">
              <span>
                Sacola de{" "}
                {cesta?.cliente?.nome
                  ? cesta.cliente.nome
                  : cesta?.cliente?.razao_social
                  ? cesta.cliente.razao_social
                  : "cliente não identificado"}
              </span>
            </div>
            <span className="select-cesta-unico">
              <SelectPedidos
                options={
                  cesta?.id_situacao ===
                  situacoes
                    .find((situacao) => situacao.name)
                    ?.value.toString()
                    ? situacoes 
                    : situacoes.filter((situacao) => situacao.name)
                }
                loading={loading}
                value={cesta?.id_situacao?.toString()}
                onChange={handleSituacaoChange}
                cores={cores}
              />
            </span> 
          </div>
          <div className="linha"></div>
          <div className="flex-container">
            <div className="titulo-cesta fonteTexto">
              Data:{" "}
              {moment(cesta?.data_modificacao).format("DD/MM/YYYY HH:mm:ss")}
            </div>
            <div className="titulo-cesta fonteTexto">Tabela: {cesta?.tabela || ""}</div>
          </div>
          <div className="container-branco">
            <div className="flex-container">
              {cesta?.cliente.nome || cesta?.cliente.razao_social ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Cliente</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    <span>
                      {cesta?.cliente.nome ||
                        cesta?.cliente.razao_social ||
                        "Não identificado"}
                    </span>
                  </div>
                </div>
              ) : null}

              {cesta?.cliente.telefone ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Telefone</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    <a
                      target="blank"
                      href={`https://api.whatsapp.com/send?phone=55${cesta?.cliente.telefone}`}
                    >
                      {phoneMask(cesta?.cliente.telefone)}
                    </a>
                  </div>
                </div>
              ) : null}

              {cesta?.cliente.email ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Email</div>
                  <div className="subtitulo-cesta fonteSubtitulo">{cesta?.cliente.email}</div>
                </div>
              ) : null}
            </div>

            <div className="flex-container">
              {cesta?.cliente.cpf || cesta?.cliente.cnpj ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">CPF/CNPJ</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    {cpfMask(cesta?.cliente.cpf || "") ||
                      cnpjMask(cesta?.cliente.cnpj || "")}
                  </div>
                </div>
              ) : null}

              {cesta?.cliente.inscricao_estadual ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Inscrição Estadual</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    {cesta?.cliente.inscricao_estadual}
                  </div>
                </div>
              ) : null}

              {cesta?.cliente.data_nascimento ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Data de nascimento</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    {moment(cesta?.cliente.data_nascimento).format(
                      "DD/MM/YYYY",
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="linha-menor"></div>

            <div className="flex-container">
              {cesta?.cliente.cep ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">CEP</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    {cepMask(cesta?.cliente.cep)}
                  </div>
                </div>
              ) : null}
              {cesta?.cliente.cidade && cesta?.cliente.estado ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Cidade</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    {cesta?.cliente.cidade} / {cesta?.cliente.estado}
                  </div>
                </div>
              ) : null}
              {cesta?.cliente.bairro ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Bairro</div>
                  <div className="subtitulo-cesta fonteSubtitulo">{cesta?.cliente.bairro}</div>
                </div>
              ) : null}

              {cesta?.cliente.endereco || cesta?.cliente.numero ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Endereço</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    {cesta?.cliente.endereco}, {cesta?.cliente.numero}
                    {cesta?.cliente.complemento
                      ? `, ${cesta?.cliente.complemento}`
                      : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="container-branco">
            <div className="flex-container">
              {cesta?.catalogo?.descricao ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Catálogo</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    {cesta?.catalogo.descricao}
                  </div>
                </div>
              ) : null}
              {cesta?.vendedor ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Vendedor</div>
                  <div className="subtitulo-cesta fonteSubtitulo">{cesta?.vendedor}</div>
                </div>
              ) : null}
              {cesta?.catalogo?.identificacao ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Código do catálogo</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    {cesta?.catalogo.identificacao}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="container-branco">
            <div className="flex-container">
              {cesta?.desconto_progressivo?.valor && cesta.desconto_progressivo.valor > 0 ? (
                <>
                  <div className="column-container">
                    <div className="titulo-cesta fonteTexto">
                      Desconto Progressivo | -{cesta.desconto_progressivo.percentual}%
                    </div>
                    <div className="subtitulo-cesta fonteSubtitulo">
                      {`${cesta.moeda} ${cesta.desconto_progressivo.valor
                        .toFixed(2)
                        .replace(".", ",")}`}
                    </div>
                  </div>
                </>
              ) : null}
              {cesta?.cupom ? (
                <>
                  <div className="column-container">
                    <div className="titulo-cesta fonteTexto">Cupom aplicado</div>
                    <div className="subtitulo-cesta fonteSubtitulo">
                      {cesta.cupom.cupom}
                    </div>
                  </div>
                  <div className="column-container">
                    <div className="titulo-cesta fonteTexto">Desconto</div>
                    <div className="subtitulo-cesta fonteSubtitulo">
                      {cesta.cupom.tipoDesconto === 'porcentagem'
                        ? `${cesta.cupom.desconto / 100}%`
                        : `${cesta.moeda} ${(cesta.cupom.desconto / 100).toFixed(2).replace(".", ",")}`
                      }
                    </div>
                  </div>
                </>
              ) : null}
              {cesta?.frete ? (
                <>
                  <div className="column-container">
                    <div className="titulo-cesta fonteTexto">Frete</div>
                    <div className="subtitulo-cesta fonteSubtitulo">
                      {cesta.frete.descricao}
                    </div>
                  </div>
                  {cesta.frete.preco !== undefined ? (
                    <div className="column-container">
                      <div className="titulo-cesta fonteTexto">Valor do frete</div>
                      <div className="subtitulo-cesta fonteSubtitulo">
                        {cesta.moeda} {(cesta.frete.preco / 100).toFixed(2).replace(".", ",")}
                      </div>
                    </div>
                  ) : null}
                  {cesta.frete.diasEstimados ? (
                    <div className="column-container">
                      <div className="titulo-cesta fonteTexto">Prazo</div>
                      <div className="subtitulo-cesta fonteSubtitulo">
                        {cesta.frete.diasEstimados} {cesta.frete.diasEstimados === 1 ? "dia útil" : "dias úteis"}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
              {cesta?.valor_total ? (
                <div className="column-container">
                  <div className="titulo-cesta fonteTexto">Valor da sacola</div>
                  <div className="subtitulo-cesta fonteSubtitulo">
                    <span>
                      {cesta.moeda} {(() => {
                        let total = cesta.valor_total;
                        if (cesta.desconto_progressivo?.valor) {
                          total = total - cesta.desconto_progressivo.valor;
                        }
                        if (cesta.cupom) {
                          if (cesta.cupom.tipoDesconto === 'porcentagem') {
                            total = total - (total * cesta.cupom.desconto / 10000);
                          } else {
                            total = total - (cesta.cupom.desconto / 100);
                          }
                        }
                        if (cesta.frete?.preco) {
                          total += cesta.frete.preco / 100;
                        }
                        return total.toFixed(2).replace(".", ",");
                      })()}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="container-branco">
            <TableProdutos
              produtos={cesta?.produtos || []}
              moeda={cesta?.moeda || ""}
              template="principal"
            />
          </div>

          <div className="container-branco">
            {cesta?.cliente.observacao && (
              <>
                <div className="flex-container">
                  <div className="column-container">
                    <div className="titulo-cesta fonteTexto">Observação da sacola</div>
                    <div className="subtitulo-cesta fonteSubtitulo">
                      {cesta?.cliente.observacao}
                    </div>
                  </div>
                </div>
                <div className="linha-menor"></div>
              </>
            )}
            <div className="flex-container">
              <div className="titulo-cesta fonteTexto">Observação interna</div>
            </div>
            <Form
              id={"form-gravar-obs-cesta"}
              setFormValid={setFormValidObs}
              onSubmit={() =>
                handleOnSubmitObservacaoInterna(observacaoInterna)
              }
            >
              <div className="obs-gravar">
                <div className="input-div">
                  <Input
                    placeholder="Insira aqui a observação sobre essa sacola. Essa observação não fica visível para o cliente."
                    onChange={(newValue) => setObservacaoInterna(newValue)}
                    value={observacaoInterna}
                    type={"text"}
                  />
                </div>
                <Button type="submit" title="Gravar" className="fonteTexto btnGravar" />
              </div>
            </Form>
          </div>
          <FooterPaginas>
            <div className="flex-botoes-cesta">
              <Button
                loading={loadingPrinting}
                className="botao-bottom fonteTexto"
                padding="5rem"
                title="Imprimir sacola"
                onClick={() => handlePrint()}
                onOptionsClick={() => setModalLayoutOpen(true)}
                optionsTitle="Configurar layout de impressão"
              />
              <div className="flex-direita">
              <Button
                className="botao-bottom fonteTexto"
                padding="5rem"
                title="Retomar sacola com dados do cliente"
                  onClick={() => {
                    // Ensure dominio_https always has a fallback value
                    const dominioHttps = empresa?.dominio_https || 'https://app.conectavenda.com.br/';
                    // Ensure the domain ends with a slash
                    const normalizedDomain = dominioHttps.endsWith('/') ? dominioHttps : `${dominioHttps}/`;

                    const link = `${normalizedDomain}${cesta!.catalogo.identificacao}?cesta=${cesta!.identificacao_cesta}${
                      cesta!.cliente.nome !== "Cliente não identificado" ? "&tipo_cesta=C" : ""
                    }`;

                    navigator.clipboard.writeText(link).then(() => {
                      Swal.fire({
                        title: "Link copiado!",
                        text: "O link da sacola foi copiado para a área de transferência.",
                        icon: "success",
                        confirmButtonText: "OK",
                      });
                    }).catch(() => {
                      Swal.fire({
                        title: "Erro",
                        text: "Ocorreu um erro ao copiar o link. Tente novamente.",
                        icon: "error",
                        confirmButtonText: "OK",
                      });
                    });
                  }}
                />
                <PopoverInfo
                  className="popover1"
                  width="78rem"
                  mensagem="Link da sacola do cliente para você enviar para ele, já virá pré preenchido com as informações que ele adicionou."
                />
                <Button
                  className="botao-bottom fonteTexto"
                  padding="5rem"
                  title="Retomar sacola sem dados do cliente"
                  onClick={() => {
                    // Ensure dominio_https always has a fallback value
                    const dominioHttps = empresa?.dominio_https || 'https://app.conectavenda.com.br/';
                    // Ensure the domain ends with a slash
                    const normalizedDomain = dominioHttps.endsWith('/') ? dominioHttps : `${dominioHttps}/`;

                    const link = `${normalizedDomain}${cesta!.catalogo.identificacao}?cesta=${cesta!.identificacao_cesta}`;

                    navigator.clipboard.writeText(link).then(() => {
                      Swal.fire({
                        title: "Link copiado!",
                        text: "O link da sacola foi copiado para a área de transferência.",
                        icon: "success",
                        confirmButtonText: "OK",
                      });
                    }).catch(() => {
                      Swal.fire({
                        title: "Erro",
                        text: "Ocorreu um erro ao copiar o link. Tente novamente.",
                        icon: "error",
                        confirmButtonText: "OK",
                      });
                    });
                  }}
                />
                <PopoverInfo
                  className="popover2"
                  width="78rem"
                  mensagem="Para você retomar a sacola do cliente, os campos com os dados virá em branco para que você preencha."
                />
              </div>
            </div>
          </FooterPaginas>
        </ContainerCesta>
      )}
      <PrintableSacola sacola={memoizedSacola} ref={printableRef} layoutConfig={layoutConfig} ordenacao={ordenacaoProdutos} />
      <ModalLayoutImpressao
        isOpen={modalLayoutOpen}
        onClose={handleModalClose}
        tipo="sacola"
      />
    </>
  );
};

export default Cesta;
